/**
 * TUVI - Componente Subir Excel
 * Permite cargar archivo Excel y crear proyecto con capítulos automáticamente
 */

import React, { useState } from 'react';
import { parseExcelCotizacion } from '../../services/excelParser';
import { crearProyecto, verificarProyectoExistente, actualizarProyectoActividades, reiniciarProyecto } from '../../services/api';
import './SubirExcel.css';

const SubirExcel = () => {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedCapitulos, setExpandedCapitulos] = useState({});

  // Estados para modal de proyecto existente
  const [showModalExistente, setShowModalExistente] = useState(false);
  const [proyectoExistente, setProyectoExistente] = useState(null);

  /**
   * Procesar archivo Excel
   */
  const processFile = async (file) => {
    if (!file) return;

    // Validar extensión
    const validExtensions = ['.xlsx', '.xls'];
    const fileName = file.name.toLowerCase();
    const isValid = validExtensions.some(ext => fileName.endsWith(ext));

    if (!isValid) {
      setError('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const datosExcel = await parseExcelCotizacion(file);
      setDatos(datosExcel);
      console.log('Datos extraídos:', datosExcel);
    } catch (err) {
      setError(err.message || 'Error al procesar el archivo');
      console.error('Error al leer Excel:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Manejar carga de archivo Excel
   */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  /**
   * Manejar drag and drop
   */
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  /**
   * Click en la zona de drop
   */
  const handleDropZoneClick = () => {
    if (!loading) {
      document.getElementById('file-upload').click();
    }
  };

  /**
   * Toggle expandir/colapsar capítulo
   */
  const toggleCapitulo = (numero) => {
    setExpandedCapitulos(prev => ({
      ...prev,
      [numero]: !prev[numero]
    }));
  };

  /**
   * Crear proyecto en la base de datos
   * Primero verifica si existe un proyecto similar
   */
  const handleCrearProyecto = async () => {
    if (!datos) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Verificar si existe proyecto similar
      const verificacion = await verificarProyectoExistente(datos.proyecto);

      if (verificacion.data.existe && verificacion.data.proyectos?.length > 0) {
        // Mostrar modal preguntando que hacer
        setProyectoExistente(verificacion.data.proyectos[0]);
        setShowModalExistente(true);
        setLoading(false);
        return;
      }

      // Si no existe, crear normalmente
      await crearProyectoNuevo();

    } catch (err) {
      setError(err.message || 'Error al verificar el proyecto');
      console.error('Error:', err);
      setLoading(false);
    }
  };

  /**
   * Crear proyecto nuevo (flujo normal)
   */
  const crearProyectoNuevo = async () => {
    setLoading(true);
    try {
      const response = await crearProyecto(datos);
      console.log('Respuesta del servidor:', response.data);
      setSuccess(`¡Proyecto creado exitosamente! ID: ${response.data.proyecto_id} | ${response.data.capitulos_creados} capítulos | ${response.data.actividades_creadas} actividades`);

      // Limpiar datos después de 3 segundos
      setTimeout(() => {
        setDatos(null);
        setSuccess(null);
      }, 3000);

    } catch (err) {
      setError(err.message || 'Error al crear el proyecto');
      console.error('Error al crear proyecto:', err);
    } finally {
      setLoading(false);
      setShowModalExistente(false);
      setProyectoExistente(null);
    }
  };

  /**
   * Actualizar proyecto existente (agregar solo actividades nuevas)
   */
  const handleActualizarExistente = async () => {
    if (!proyectoExistente || !datos) return;

    setLoading(true);
    setShowModalExistente(false);

    try {
      const response = await actualizarProyectoActividades(proyectoExistente.id, datos);
      console.log('Respuesta del servidor:', response.data);

      const actividades = response.data.actividades_agregadas || 0;
      const mensaje = actividades > 0
        ? `¡Proyecto actualizado! Se agregaron ${actividades} actividades nuevas.`
        : 'El proyecto ya tenia todas las actividades. No se agregaron nuevas.';

      setSuccess(mensaje);

      // Limpiar datos después de 3 segundos
      setTimeout(() => {
        setDatos(null);
        setSuccess(null);
      }, 3000);

    } catch (err) {
      setError(err.message || 'Error al actualizar el proyecto');
      console.error('Error al actualizar proyecto:', err);
    } finally {
      setLoading(false);
      setProyectoExistente(null);
    }
  };

  /**
   * Reiniciar proyecto existente (borrar actividades actuales y cargar nuevas)
   */
  const handleReiniciarProyecto = async () => {
    if (!proyectoExistente || !datos) return;

    setLoading(true);
    setShowModalExistente(false);

    try {
      const response = await reiniciarProyecto(proyectoExistente.id, datos);
      console.log('Respuesta del servidor:', response.data);

      const capitulosCreados = response.data.capitulos_creados || 0;
      const actividadesCreadas = response.data.actividades_creadas || 0;
      const mensaje = `Proyecto reiniciado exitosamente. Se cargaron ${capitulosCreados} capitulos y ${actividadesCreadas} actividades nuevas.`;

      setSuccess(mensaje);

      // Limpiar datos despues de 3 segundos
      setTimeout(() => {
        setDatos(null);
        setSuccess(null);
      }, 3000);

    } catch (err) {
      setError(err.message || 'Error al reiniciar el proyecto');
      console.error('Error al reiniciar proyecto:', err);
    } finally {
      setLoading(false);
      setProyectoExistente(null);
    }
  };

  /**
   * Cerrar modal y cancelar
   */
  const handleCerrarModal = () => {
    setShowModalExistente(false);
    setProyectoExistente(null);
  };

  /**
   * Limpiar formulario
   */
  const handleLimpiar = () => {
    setDatos(null);
    setError(null);
    setSuccess(null);
    setExpandedCapitulos({});
  };

  /**
   * Formatear número como moneda
   */
  const formatMoney = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  /**
   * Calcular total de actividades
   */
  const getTotalActividades = () => {
    if (!datos?.capitulos) return 0;
    return datos.capitulos.reduce((total, cap) => total + (cap.actividades?.length || 0), 0);
  };

  return (
    <div className="subir-excel-container">
      {/* Header compacto */}
      <div className="page-header">
        <h1>Crear Proyecto desde Excel</h1>
        <p>Importa tu archivo de cotización para crear un proyecto con capítulos</p>
      </div>

      {/* Mensajes de error */}
      {error && (
        <div className="alert alert-error">
          <svg className="alert-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div className="alert-content">
            <strong>Error</strong>
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Mensajes de éxito */}
      {success && (
        <div className="alert alert-success">
          <svg className="alert-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="alert-content">
            <strong>Proyecto creado</strong>
            <p>{success}</p>
          </div>
        </div>
      )}

      {/* Upload Zone */}
      {!datos && !success && (
        <div className="upload-card">
          <div
            className={`drop-zone ${isDragging ? 'dragging' : ''} ${loading ? 'loading' : ''}`}
            onClick={handleDropZoneClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
          >
            <input
              id="file-upload"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={loading}
              className="file-input"
            />

            {loading ? (
              <div className="upload-loading">
                <div className="spinner"></div>
                <p className="loading-text">Procesando archivo...</p>
                <p className="loading-subtext">Extrayendo datos del Excel</p>
              </div>
            ) : (
              <>
                <div className="drop-zone-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <h3>Arrastra tu archivo aquí</h3>
                <p className="drop-zone-text">o haz clic para seleccionar</p>
                <div className="btn-upload-display">
                  Seleccionar Archivo
                </div>
                <p className="file-types">.xlsx, .xls</p>
              </>
            )}
          </div>

          <div className="info-card">
            <h4>Formato requerido</h4>
            <ul>
              <li>Datos del cliente en C16-C18</li>
              <li>Datos del inmueble en G16-G18</li>
              <li>8 capítulos con actividades</li>
              <li>Totales en G389-G393</li>
            </ul>
          </div>
        </div>
      )}

      {/* Preview de datos extraídos */}
      {datos && !success && (
        <div className="preview-container">
          <div className="preview-header">
            <div className="preview-header-content">
              <h2>Datos Extraídos</h2>
              <p>Revisa la información antes de crear el proyecto</p>
            </div>
            <div className="preview-badge">
              <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Listo
            </div>
          </div>

          {/* Información del Proyecto */}
          <div className="section">
            <h3>Información del Proyecto</h3>
            <div className="data-grid">
              <div className="data-item">
                <span className="label">Cliente</span>
                <span className="value">{datos.proyecto.nombre_cliente}</span>
              </div>
              <div className="data-item">
                <span className="label">Proyecto</span>
                <span className="value">{datos.proyecto.proyecto_nombre}</span>
              </div>
              <div className="data-item">
                <span className="label">Área (m²)</span>
                <span className="value">{datos.proyecto.area_m2}</span>
              </div>
              <div className="data-item">
                <span className="label">Apartamento</span>
                <span className="value">{datos.proyecto.apartamento}</span>
              </div>
              <div className="data-item">
                <span className="label">Teléfono</span>
                <span className="value">{datos.proyecto.telefono}</span>
              </div>
              <div className="data-item">
                <span className="label">Correo</span>
                <span className="value">{datos.proyecto.correo}</span>
              </div>
            </div>
          </div>

          {/* Valores Financieros */}
          <div className="section financial-section">
            <h3>Valores Financieros</h3>
            <div className="financial-grid">
              <div className="financial-item">
                <span className="financial-label">Subtotal</span>
                <span className="financial-value">{formatMoney(datos.proyecto.subtotal)}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">AIU (15%)</span>
                <span className="financial-value">{formatMoney(datos.proyecto.aiu)}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">IVA</span>
                <span className="financial-value">{formatMoney(datos.proyecto.iva)}</span>
              </div>
              <div className="financial-item total-item">
                <span className="financial-label">VALOR FINAL</span>
                <span className="financial-value total">{formatMoney(datos.proyecto.valor_final)}</span>
              </div>
            </div>
          </div>

          {/* Capítulos */}
          <div className="section">
            <h3>Capítulos ({datos.capitulos?.length || 0}) - {getTotalActividades()} actividades</h3>
            <div className="capitulos-list">
              {datos.capitulos?.map((capitulo) => (
                <div key={capitulo.numero} className={`capitulo-card ${capitulo.subtotal === 0 ? 'empty' : ''}`}>
                  <div
                    className="capitulo-header"
                    onClick={() => toggleCapitulo(capitulo.numero)}
                  >
                    <div className="capitulo-info">
                      <span className="capitulo-numero">{capitulo.numero}</span>
                      <div className="capitulo-details">
                        <span className="capitulo-nombre">{capitulo.nombre}</span>
                        <span className="capitulo-meta">
                          {capitulo.actividades?.length || 0} actividades
                        </span>
                      </div>
                    </div>
                    <div className="capitulo-values">
                      <span className="capitulo-porcentaje-num">{capitulo.porcentaje.toFixed(1)}%</span>
                      <span className="capitulo-subtotal">{formatMoney(capitulo.subtotal)}</span>
                      <span className={`capitulo-toggle ${expandedCapitulos[capitulo.numero] ? 'expanded' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </div>

                  {expandedCapitulos[capitulo.numero] && capitulo.actividades?.length > 0 && (
                    <div className="capitulo-actividades">
                      <table className="actividades-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Descripción</th>
                            <th>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {capitulo.actividades.map((act, idx) => (
                            <tr key={idx}>
                              <td className="act-orden">{act.orden}</td>
                              <td className="act-descripcion">{act.descripcion}</td>
                              <td className="act-valor">{formatMoney(act.valor)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="actions">
            <button
              onClick={handleLimpiar}
              disabled={loading}
              className="btn btn-cancel"
            >
              Cancelar
            </button>
            <button
              onClick={handleCrearProyecto}
              disabled={loading}
              className="btn btn-create"
            >
              {loading ? (
                <>
                  <span className="btn-spinner"></span> Creando...
                </>
              ) : (
                'Crear Proyecto'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Modal de Proyecto Existente */}
      {showModalExistente && proyectoExistente && (
        <div className="modal-overlay">
          <div className="modal-existente">
            <div className="modal-header">
              <svg className="modal-icon warning" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h3>Proyecto Existente Detectado</h3>
            </div>

            <div className="modal-body">
              <p>Se encontro un proyecto similar en el sistema:</p>

              <div className="proyecto-existente-info">
                <div className="info-row">
                  <span className="info-label">Proyecto:</span>
                  <span className="info-value">{proyectoExistente.proyecto_nombre}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Cliente:</span>
                  <span className="info-value">{proyectoExistente.nombre_cliente}</span>
                </div>
                {proyectoExistente.apartamento && (
                  <div className="info-row">
                    <span className="info-label">Apartamento:</span>
                    <span className="info-value">{proyectoExistente.apartamento}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Valor:</span>
                  <span className="info-value">{formatMoney(proyectoExistente.valor_final)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Estado:</span>
                  <span className={`info-value estado-${proyectoExistente.estado}`}>
                    {proyectoExistente.estado?.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>

              <p className="modal-question">¿Que deseas hacer?</p>
            </div>

            <div className="modal-actions">
              <button
                onClick={handleActualizarExistente}
                className="btn btn-update"
                disabled={loading}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
                Actualizar Existente
              </button>
              <button
                onClick={handleReiniciarProyecto}
                className="btn btn-restart"
                disabled={loading}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
                Reiniciar Proyecto
              </button>
              <button
                onClick={crearProyectoNuevo}
                className="btn btn-create-new"
                disabled={loading}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Crear Nuevo Proyecto
              </button>
              <button
                onClick={handleCerrarModal}
                className="btn btn-cancel-modal"
                disabled={loading}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubirExcel;
